import { BaseStore } from "./base.svelte";
import { type Map as MaplibreMap, LngLatBounds } from "maplibre-gl";

interface LocationCoords {
  lat: number;
  lng: number;
}

interface ViewportBounds {
  north: number;
  south: number;
  east: number;
  west: number;
  center: LocationCoords;
  zoom: number;
}

class LocationStore extends BaseStore {
  // User's actual location
  userLocation = $state<LocationCoords | null>(null);
  userLocationAccuracy = $state<number | null>(null);
  isTrackingUser = $state(false);
  locationPermission = $state<PermissionState | null>(null);

  // Map viewport state
  mapCenter = $state<LocationCoords | null>(null);
  mapBounds = $state<ViewportBounds | null>(null);
  mapZoom = $state<number>(15);

  selectedPoiId = $state<string | null>(null);

  // Derived states
  distanceFromUser = $derived.by(() => {
    if (!this.userLocation || !this.mapCenter) return null;
    return this.calculateDistance(this.userLocation, this.mapCenter);
  });

  isUserInViewport = $derived.by(() => {
    if (!this.userLocation || !this.mapBounds) return false;
    return this.isLocationInBounds(this.userLocation, this.mapBounds);
  });

  private watchId: number | null = null;
  private mapInstance: MaplibreMap | null = null;
  private moveEndHandler: (() => void) | null = null;

  constructor() {
    super();
    this.checkLocationPermission();
  }

  setMapInstance(map: MaplibreMap) {
    this.mapInstance = map;
    this.initializeMapTracking(map);
  }

  setSelectedPoi(id: string | null) {
    this.selectedPoiId = id;

    // Update the map styling
    if (this.mapInstance) {
      // You'll need to pass the MapManager instance to locationStore
      // or call the update method directly
      this.updateMapStyling(id);
    }
  }

  private updateMapStyling(selectedPoiId: string | null) {
    if (!this.mapInstance || !this.mapInstance.loaded()) return;

    const numericId = selectedPoiId ? Number(selectedPoiId) : null;

    if (numericId) {
      // Create a nice shadow effect
      this.mapInstance.setFilter("highlighted-point-shadow", [
        "==",
        ["get", "id"],
        numericId,
      ]);

      // Make it look like a proper shadow
      this.mapInstance.setPaintProperty(
        "highlighted-point-shadow",
        "circle-radius",
        50
      );
      this.mapInstance.setPaintProperty(
        "highlighted-point-shadow",
        "circle-color",
        "rgba(0, 0, 0, 0.95)"
      );
      this.mapInstance.setPaintProperty(
        "highlighted-point-shadow",
        "circle-blur",
        1.5
      ); // Soft edges
      this.mapInstance.setPaintProperty(
        "highlighted-point-shadow",
        "circle-stroke-width",
        0
      ); // No stroke

      this.mapInstance.setFilter("highlighted-point", [
        "==",
        ["get", "id"],
        numericId,
      ]);

      // Hide from regular layer
      this.mapInstance.setFilter("unclustered-point", [
        "all",
        ["!", ["has", "point_count"]],
        ["!=", ["get", "id"], numericId],
      ]);
    } else {
      this.mapInstance.setFilter("highlighted-point-shadow", [
        "==",
        ["get", "id"],
        -1,
      ]);
      this.mapInstance.setFilter("highlighted-point", [
        "==",
        ["get", "id"],
        -1,
      ]);
      this.mapInstance.setFilter("unclustered-point", [
        "!",
        ["has", "point_count"],
      ]);
    }

    this.mapInstance.triggerRepaint();
  }

  // Initialize map tracking
  private initializeMapTracking(map: MaplibreMap) {
    // Set up move end listener
    this.moveEndHandler = () => {
      this.updateMapState();
    };

    // Listen to map changes
    map.on("moveend", this.moveEndHandler);
    map.on("zoomend", this.moveEndHandler);

    // Initial update
    this.updateMapState();
  }

  // Clean up map tracking
  disposeMapTracking() {
    if (this.mapInstance && this.moveEndHandler) {
      this.mapInstance.off("moveend", this.moveEndHandler);
      this.mapInstance.off("zoomend", this.moveEndHandler);
    }
    this.moveEndHandler = null;
  }

  // Update map viewport state
  private updateMapState() {
    if (!this.mapInstance) return;

    const center = this.mapInstance.getCenter();
    const zoom = this.mapInstance.getZoom();
    const bounds = this.mapInstance.getBounds();

    this.mapCenter = { lat: center.lat, lng: center.lng };
    this.mapZoom = zoom;

    if (bounds) {
      this.mapBounds = {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
        center: { lat: center.lat, lng: center.lng },
        zoom,
      };
    }
  }

  // Check browser location permission
  async checkLocationPermission() {
    if ("permissions" in navigator) {
      try {
        const result = await navigator.permissions.query({
          name: "geolocation",
        });
        this.locationPermission = result.state;

        result.addEventListener("change", () => {
          this.locationPermission = result.state;
        });
      } catch (error) {
        console.warn("Unable to check location permission:", error);
      }
    }
  }

  // Start tracking user location
  async startTrackingUserLocation(): Promise<boolean> {
    if (!("geolocation" in navigator)) {
      this.error = "Geolocation is not supported by this browser";
      return false;
    }

    this.isTrackingUser = true;

    // Get initial position
    try {
      const position = await this.getCurrentPosition();
      this.updateUserLocation(position);
      this.addUserLocationMarker();
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to get location";
      this.isTrackingUser = false;
      return false;
    }

    // Watch for position changes
    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.updateUserLocation(position);
        this.updateUserLocationMarker();
      },
      (error) => {
        console.error(error);
      },
      {
        enableHighAccuracy: false, // Start with lower accuracy for faster response
        timeout: 15000, // Increase timeout to 15 seconds
        maximumAge: 30000, // Allow cached positions up to 30 seconds old
      }
    );

    return true;
  }

  // Stop tracking user location
  stopTrackingUserLocation() {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    this.isTrackingUser = false;
    this.removeUserLocationMarker();
  }

  private async addUserLocationMarker() {
    if (!this.mapInstance || !this.userLocation) return;

    // First ensure the butterfly icon is loaded
    await this.ensureUserLocationIcon();

    // Get the separate user location source
    const userLocationSource = this.mapInstance.getSource(
      "user-location"
    ) as any;

    if (userLocationSource) {
      // Update the separate user location source
      userLocationSource.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [this.userLocation.lng, this.userLocation.lat],
            },
            properties: {
              id: "user-location",
              name: "Your Location",
              subCategory: "user_location",
              label: "USER_LOCATION",
              isUserLocation: true,
              accuracy: this.userLocationAccuracy,
            },
          },
        ],
      });

      // Force a map repaint
      if (this.mapInstance.loaded()) {
        this.mapInstance.triggerRepaint();
      }
    }
  }

  private async ensureUserLocationIcon() {
    if (!this.mapInstance) return;

    const iconId = "user_location-USER_LOCATION-icon";

    // If already loaded, return early
    if (this.mapInstance.hasImage(iconId)) {
      return;
    }

    try {
      // Load butterfly icon
      const img = new Image();
      const iconLoaded = new Promise((resolve, reject) => {
        img.onload = async () => {
          try {
            const canvas = document.createElement("canvas");
            const size = 64;
            canvas.width = size;
            canvas.height = size;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
              reject(new Error("Could not get canvas context"));
              return;
            }

            ctx.drawImage(img, 0, 0, size, size);
            const bitmap = await createImageBitmap(canvas);

            // Double-check before adding (race condition protection)
            if (!this.mapInstance!.hasImage(iconId)) {
              this.mapInstance!.addImage(iconId, bitmap, { pixelRatio: 2 });
            }
            resolve(true);
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => reject(new Error("Failed to load butterfly icon"));
        img.src = "/assets/bee.svg";
      });

      await iconLoaded;
    } catch (error) {
      console.error("Failed to load user location icon:", error);
    }
  }

  // Update user location marker
  private updateUserLocationMarker() {
    if (!this.userLocation) return;
    this.addUserLocationMarker(); // This will update the existing marker
  }

  private removeUserLocationMarker() {
    const userLocationSource = this.mapInstance?.getSource(
      "user-location"
    ) as any;
    if (userLocationSource) {
      userLocationSource.setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  }

  // Get current position as promise
  private getCurrentPosition(
    highAccuracy: boolean = false
  ): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: highAccuracy,
        timeout: highAccuracy ? 15000 : 10000,
        maximumAge: highAccuracy ? 0 : 30000,
      });
    });
  }

  // Update user location from GeolocationPosition
  private updateUserLocation(position: GeolocationPosition) {
    const { latitude, longitude, accuracy } = position.coords;
    this.userLocation = { lat: latitude, lng: longitude };
    this.userLocationAccuracy = accuracy;
    this.error = null;
  }

  // Center map on user location
  centerMapOnUser(animate: boolean = true) {
    if (!this.mapInstance || !this.userLocation) return;

    if (animate) {
      this.mapInstance.flyTo({
        center: [this.userLocation.lng, this.userLocation.lat],
        duration: 500,
        zoom: 18,
      });
    } else {
      this.mapInstance.setCenter([
        this.userLocation.lng,
        this.userLocation.lat,
      ]);
      this.mapInstance.setZoom(18);
    }
  }

  // Center map on specific location
  centerMapOn(
    location: LocationCoords,
    zoom?: number,
    animate: boolean = true
  ) {
    if (!this.mapInstance) return;

    if (animate) {
      this.mapInstance.flyTo({
        center: [location.lng, location.lat],
        zoom: zoom || this.mapZoom,
        duration: 500,
      });
    } else {
      this.mapInstance.setCenter([location.lng, location.lat]);
      if (zoom) this.mapInstance.setZoom(zoom);
    }
  }

  // Fit map to show both user and a target location
  fitMapToShowUserAndTarget(target: LocationCoords, padding: number = 50) {
    if (!this.mapInstance || !this.userLocation) return;

    const bounds = new LngLatBounds([
      [this.userLocation.lng, this.userLocation.lat],
      [target.lng, target.lat],
    ]);

    this.mapInstance.fitBounds(bounds, {
      padding: padding,
      duration: 500,
    });
  }

  // Calculate distance between two points (in meters)
  public calculateDistance(loc1: LocationCoords, loc2: LocationCoords): number {
    const R = 6371000; // Earth's radius in meters
    const φ1 = (loc1.lat * Math.PI) / 180;
    const φ2 = (loc2.lat * Math.PI) / 180;
    const Δφ = ((loc2.lat - loc1.lat) * Math.PI) / 180;
    const Δλ = ((loc2.lng - loc1.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  // Check if location is within bounds
  private isLocationInBounds(
    location: LocationCoords,
    bounds: ViewportBounds
  ): boolean {
    return (
      location.lat >= bounds.south &&
      location.lat <= bounds.north &&
      location.lng >= bounds.west &&
      location.lng <= bounds.east
    );
  }

  // Format distance for display
  formatDistance(meters: number | null): string {
    if (meters === null) return "";

    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    } else {
      return `${(meters / 1000).toFixed(1)}km`;
    }
  }

  // Get user-friendly error message
  private getGeolocationErrorMessage(error: GeolocationPositionError): string {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return "Location permission denied";
      case error.POSITION_UNAVAILABLE:
        return "Location information unavailable";
      case error.TIMEOUT:
        return "Location request timed out";
      default:
        return "Unknown location error";
    }
  }

  // Get map state for saving/restoring
  getMapState(): ViewportBounds | null {
    return this.mapBounds;
  }

  // Restore map state
  restoreMapState(state: ViewportBounds) {
    if (!this.mapInstance) return;

    this.mapInstance.setCenter([state.center.lng, state.center.lat]);
    this.mapInstance.setZoom(state.zoom);
  }

  // Clear all data
  clear() {
    this.stopTrackingUserLocation();
    this.disposeMapTracking();
    this.userLocation = null;
    this.userLocationAccuracy = null;
    this.mapCenter = null;
    this.mapBounds = null;
    this.error = null;
  }

  // Zoom functions for MapLibre
  zoomIn() {
    if (this.mapInstance) {
      const currentZoom = this.mapInstance.getZoom();
      this.mapInstance.easeTo({
        zoom: currentZoom + 1,
        duration: 250,
      });
    }
  }

  zoomOut() {
    if (this.mapInstance) {
      const currentZoom = this.mapInstance.getZoom();
      this.mapInstance.easeTo({
        zoom: currentZoom - 1,
        duration: 250,
      });
    }
  }
}

export const locationStore = new LocationStore();
